<script setup lang="ts">
import type { InventoryItem } from '../stores/inventory'

const props = defineProps<{
  item: InventoryItem
  canEdit?: boolean
}>()

defineEmits<{
  (e: 'click'): void
  (e: 'edit'): void
  (e: 'delete'): void
}>()

const API_BASE = import.meta.env.VITE_API_URL || ''

function getConditionColor(condition: string | null): string {
  switch (condition) {
    case 'excellent': return 'bg-green-100 text-green-800'
    case 'good': return 'bg-blue-100 text-blue-800'
    case 'fair': return 'bg-yellow-100 text-yellow-800'
    case 'poor': return 'bg-orange-100 text-orange-800'
    case 'needs_replacement': return 'bg-red-100 text-red-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

function formatCondition(condition: string | null): string {
  if (!condition) return 'Unknown'
  return condition.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}
</script>

<template>
  <div
    class="card hover:shadow-lg transition-shadow cursor-pointer"
    @click="$emit('click')"
  >
    <!-- Photo -->
    <div class="h-40 bg-gray-100 rounded-t-lg overflow-hidden -m-4 mb-3">
      <img
        v-if="item.photo_url"
        :src="`${API_BASE}${item.photo_url}`"
        :alt="item.name"
        class="w-full h-full object-cover"
      />
      <div v-else class="w-full h-full flex items-center justify-center text-gray-400">
        <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    </div>

    <!-- Content -->
    <div class="space-y-2">
      <h3 class="font-semibold text-gray-800 line-clamp-1">{{ item.name }}</h3>

      <div class="flex flex-wrap gap-2 text-sm">
        <span
          v-if="item.location_name"
          class="px-2 py-0.5 rounded bg-gray-100 text-gray-600"
        >
          {{ item.location_name }}
        </span>
        <span
          v-if="item.condition"
          :class="['px-2 py-0.5 rounded', getConditionColor(item.condition)]"
        >
          {{ formatCondition(item.condition) }}
        </span>
      </div>

      <p v-if="item.make || item.model" class="text-sm text-gray-500">
        {{ [item.make, item.model].filter(Boolean).join(' ') }}
      </p>

      <p v-if="item.description" class="text-sm text-gray-600 line-clamp-2">
        {{ item.description }}
      </p>
    </div>

    <!-- Actions (for managers) -->
    <div
      v-if="canEdit"
      class="flex gap-2 mt-4 pt-3 border-t"
      @click.stop
    >
      <button
        @click="$emit('edit')"
        class="btn btn-sm flex-1"
      >
        Edit
      </button>
      <button
        @click="$emit('delete')"
        class="btn btn-sm btn-danger"
      >
        Delete
      </button>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
